import { useEffect, useRef, useState } from 'react';

import useStyles from './Tabs.styles';

type TabsProps = {
  tabValues: { title: string; value: number }[];
  selectedTab: number;
  clickHandler: (value: number) => void;
  isOnDark: boolean;
};

const Tabs = ({
  tabValues,
  selectedTab,
  clickHandler,
  isOnDark = false,
}: TabsProps) => {
  const { classes, cx } = useStyles({ isOnDark });
  const [highlightStyle, setHighlightStyle] = useState({
    width: '0px',
    transform: 'translateX(0)',
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateHighlightPosition = () => {
      const selectedTabElement = containerRef.current?.querySelector(
        `[data-tab-value="${selectedTab}"]`
      ) as HTMLDivElement;

      if (selectedTabElement) {
        const { offsetWidth, offsetLeft } = selectedTabElement;
        setHighlightStyle({
          width: `${offsetWidth}px`,
          transform: `translateX(${offsetLeft}px)`,
        });
      }
    };

    updateHighlightPosition();
    window.addEventListener('resize', updateHighlightPosition);
    return () => window.removeEventListener('resize', updateHighlightPosition);
  }, [selectedTab]);

  return (
    <div
      className={classes.container}
      ref={containerRef}
      role='tablist'
      aria-orientation='horizontal'
    >
      <div className={classes.highlight} style={highlightStyle} />
      {tabValues.map((tab) => (
        <div
          key={tab.value}
          data-tab-value={tab.value}
          className={cx(
            classes.tab,
            selectedTab === tab.value && classes.selected
          )}
          role='tab'
          aria-selected={selectedTab === tab.value}
          tabIndex={selectedTab === tab.value ? 0 : -1}
          onClick={() => clickHandler(tab.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              clickHandler(tab.value);
            }
          }}
        >
          {tab.title}
        </div>
      ))}
    </div>
  );
};

export default Tabs;
